import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createSemester,
  updateSemester,
  clearError,
} from "../../redux/slices/semesterSlice";
import { fetchCourses } from "../../redux/slices/courseSlice";
import { toast } from "react-toastify";

const SemesterModal = ({ show, onHide, semester = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!semester;

  const { isCreating, isUpdating, error, semesters = [] } = useSelector((state) => state.semesters);
  const { courses } = useSelector((state) => state.courses);

  const [formData, setFormData] = useState({
    course_id: "",
    semester_name: "",
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    if (show) {
      if (isEditMode && semester) {
        setFormData({
          course_id: semester.course_id || "",
          semester_name: semester.semester_name || "",
        });
      } else {
        setFormData({
          course_id: "",
          semester_name: "",
        });
      }
      setValidationErrors({});
    }
  }, [show, isEditMode, semester]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.course_id) {
      errors.course_id = "Course selection is required";
    }
    if (!formData.semester_name.trim()) {
      errors.semester_name = "Semester name is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;



    const submitData = {
      course_id: formData.course_id,
      semester_name: formData.semester_name.trim(),
    };

    try {
      if (isEditMode) {
        await dispatch(updateSemester({ id: semester.id, data: submitData })).unwrap();
      } else {
        await dispatch(createSemester(submitData)).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("Semester operation failed:", err);
      toast.error(err || "Something went wrong");
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Semester" : "Create New Semester"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Course <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.course_id}
                  disabled={isSubmitting}
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.course_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.course_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Semester Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="semester_name"
                  placeholder="Enter semester name"
                  value={formData.semester_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.semester_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.semester_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
              ? "Update Semester"
              : "Create Semester"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SemesterModal;
